/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CustomerCodesPage.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Class of Services module.
----------------------------------------------------------------*/


import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Edit, Trash2, Download, Upload, Copy } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { ModulePageContainer } from '../../components/layouts';
import { Badge } from '../../components/ui/badge';
import ImportCustomerCodesModal from './ImportCustomerCodesModal';
import { useCustomerCodeList } from './hooks';
import { deleteCustomerCodeById, navigateToCustomerCodeDuplicate } from './customerCodesActions';
import { exportCustomerCodes } from './api';
import type { CustomerCode } from './types';

export default function CustomerCodesPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showError, showSuccess } = useNotifications();

  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; item: CustomerCode | null }>({
    isOpen: false,
    item: null
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
    tenantSlug
  } = useCustomerCodeList();

  // Add render functions to columns (JSX must be in .tsx file)
  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      if (col.key === 'code') {
        return {
          ...col,
          render: (_value: unknown, item: CustomerCode) => (
            <span className="font-medium text-foreground">{item.code}</span>
          )
        };
      }
      if (col.key === 'description') {
        return {
          ...col,
          render: (_value: unknown, item: CustomerCode) => (
            <span className="text-sm text-muted-foreground">{item.description || '�'}</span>
          )
        };
      }
      if (col.key === 'active') {
        return {
          ...col,
          render: (_value: unknown, item: CustomerCode) => (
            <Badge variant={item.active ? 'default' : 'secondary'}>
              {item.active ? t('customerCodes.list.status.enabled') : t('customerCodes.list.status.disabled')}
            </Badge>
          )
        };
      }
      if (col.key === 'createdAt') {
        return {
          ...col,
          render: (_value: unknown, item: CustomerCode) => {
            const date = new Date(item.createdAt);
            return Number.isNaN(date.getTime()) ? '�' : date.toLocaleString();
          }
        };
      }
      return col;
    });
  }, [baseColumns, t]);

  const handleCreate = useCallback(() => {
    if (!effectiveDomainId) {
      showError(t('customerCodes.messages.domainMissing'));
      return;
    }
    navigate('/pbx/class-of-service/customer-codes/new');
  }, [effectiveDomainId, navigate, showError, t]);

  const handleEdit = useCallback((item: CustomerCode) => {
    navigate(`/pbx/class-of-service/customer-codes/${item.uuid || item.id}`);
  }, [navigate]);

  const handleDeleteClick = useCallback((item: CustomerCode) => {
    setDeleteModal({ isOpen: true, item });
  }, []);

  const handleDuplicate = useCallback((item: CustomerCode) => {
    navigateToCustomerCodeDuplicate(navigate, item.uuid || item.id);
  }, [navigate]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.item) {
      return;
    }

    setIsDeleting(true);
    try {
      await deleteCustomerCodeById(deleteModal.item.id, tenantSlug);

      showSuccess(t('customerCodes.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, item: null });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customerCodes.messages.deleteError');
      showError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.item, refresh, showError, showSuccess, t, tenantSlug]);

  const renderActions = useMemo(() => (item: CustomerCode) => (
    <div className="flex items-center gap-2">
      <SimpleTooltip content={t('common.edit')} position="top">
        <button
          onClick={() => handleEdit(item)}
          className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.duplicate')} position="top">
        <button
          onClick={() => handleDuplicate(item)}
          className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
        </button>
      </SimpleTooltip>
      <SimpleTooltip content={t('common.delete')} position="top">
        <button
          onClick={() => handleDeleteClick(item)}
          className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60"
        >
          <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </button>
      </SimpleTooltip>
    </div>
  ), [handleDeleteClick, handleDuplicate, handleEdit, t]);

  const handleExport = useCallback(async () => {
    if (!effectiveDomainId) {
      showError(t('customerCodes.messages.domainMissing'));
      return;
    }

    setIsExporting(true);
    try {
      const csvContent = await exportCustomerCodes(tenantSlug);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `customer-codes-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showSuccess(t('customerCodes.messages.exportSuccess'));
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customerCodes.messages.exportError');
      showError(message);
    } finally {
      setIsExporting(false);
    }
  }, [effectiveDomainId, showError, showSuccess, t, tenantSlug]);

  const handleOpenImport = useCallback(() => {
    if (!effectiveDomainId) {
      showError(t('customerCodes.messages.domainMissing'));
      return;
    }
    setImportModalOpen(true);
  }, [effectiveDomainId, showError, t]);

  const handleCloseImport = useCallback(() => {
    setImportModalOpen(false);
  }, []);

  const handleImportSuccess = useCallback(() => {
    refresh();
    setImportModalOpen(false);
    showSuccess(t('customerCodes.messages.importSuccess'));
  }, [refresh, showSuccess, t]);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <div className="space-y-4">
          <DataGridSimple
            data={data}
            columns={columns}
            loading={loading}
            pagination={pagination}
            searchQuery={searchQuery}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSearch={handleSearch}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            onAdd={handleCreate}
            onEdit={handleEdit}
            onDuplicate={handleDuplicate}
            onDelete={handleDeleteClick}
            onColumnToggle={handleColumnToggle}
            title={t('customerCodes.title')}
            titleIcon={<Tag className="h-5 w-5 text-primary" />}
            addButtonText={t('common.add')}
            renderActions={renderActions}
            actions={
              <div className="flex items-center gap-2">
                <SimpleTooltip content={t('customerCodes.actions.export')}>
                  <button
                    onClick={handleExport}
                    disabled={isExporting}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('customerCodes.actions.export')}</span>
                  </button>
                </SimpleTooltip>
                <SimpleTooltip content={t('customerCodes.actions.import')}>
                  <button
                    onClick={handleOpenImport}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Upload className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('customerCodes.actions.import')}</span>
                  </button>
                </SimpleTooltip>
              </div>
            }
            emptyMessage={effectiveDomainId ? t('customerCodes.empty.description') : t('customerCodes.messages.domainMissing')}
          />
        </div>
      }
      formView={null}
    >
      <ImportCustomerCodesModal
        isOpen={importModalOpen}
        onClose={handleCloseImport}
        onSuccess={handleImportSuccess}
      />
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, item: null })}
        onConfirm={handleDeleteConfirm}
        title={t('customerCodes.messages.deleteConfirm')}
        message={t('customerCodes.messages.deleteConfirm')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
